import React from 'react';
import { Modal } from '../../components/ui';
import { normaliseText } from '../../utils/helpers';
import { MODULES, MODULE_LABELS, ACTIONS, REPORT_BITS } from './useUsers';

const ACTION_LABELS = { '': 'View', Add: 'Add', Edit: 'Edit', Delete: 'Delete' };

// Phone / APK add-member / permissions sheet: full screen, thumb-sized.
// Existing / New as a segmented switch, side as two big buttons, Event Admin as
// a tap row, and every module a small card with View / Add / Edit / Delete
// toggle buttons (no tiny table checkboxes). Logic: useUsers.
export default function UserFormMobile({ vm }) {
  const { isAdmin, modal, setModal, editing, saving, err, form, available, setF, setPerm, save, event, needEvent, isMarriage } = vm;

  const bitsOf = (m) => ACTIONS.map(a => `Can${m}${a}`);
  const allOn = (m) => bitsOf(m).every(b => !!form.perms[b]);
  const setAll = (m, on) => bitsOf(m).forEach(b => setPerm(b, on));

  return (
    <Modal
      fullScreen
      title={modal === 'add' ? 'Add Member' : `Permissions · ${editing?.Name || ''}`}
      onClose={() => setModal(null)}
      footer={<>
        <button className="btn light" onClick={() => setModal(null)}>Cancel</button>
        <button className="btn primary pp-grow" disabled={saving || needEvent} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </>}
    >
      {/* Permissions are per-event — make the scope explicit. */}
      <div className="pp-scope">
        {needEvent
          ? 'Pick an event in the top bar first — permissions are set per event.'
          : <>Permissions apply to event: <strong>{event?.EventName}</strong></>}
      </div>
      {modal === 'add' && (
        <>
          <div className="pp-seg" style={{ marginBottom: 12 }}>
            <button type="button" style={{ padding: 11 }} className={form.addMode === 'existing' ? 'on' : ''} onClick={() => setF('addMode', 'existing')}>Existing user</button>
            <button type="button" style={{ padding: 11 }} className={form.addMode === 'new' ? 'on' : ''} onClick={() => setF('addMode', 'new')}>New user</button>
          </div>
          {form.addMode === 'existing' ? (
            <label className="field">Select user
              <select value={form.UserId ?? ''} onChange={(e) => setF('UserId', e.target.value)}>
                <option value="">- select -</option>
                {available.map(u => (
                  <option key={u.UserId} value={u.UserId}>{`${u.FullName || u.Name} · ${u.MobileNumber || ''}`}</option>
                ))}
              </select>
            </label>
          ) : (
            <>
              <label className="field">Name *
                <input value={form.Name ?? ''} autoComplete="off" onChange={(e) => setF('Name', normaliseText(e.target.value))} />
              </label>
              <label className="field">Surname
                <input value={form.Surname ?? ''} autoComplete="off" onChange={(e) => setF('Surname', normaliseText(e.target.value))} />
              </label>
              <div className="pp-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="field">Mobile *
                  <input type="tel" inputMode="numeric" placeholder="10 digits" value={form.MobileNumber ?? ''}
                    onChange={(e) => setF('MobileNumber', e.target.value.replace(/\D/g, '').slice(0, 10))} />
                </label>
                <label className="field">Password *
                  <input value={form.Password ?? ''} autoComplete="off" onChange={(e) => setF('Password', e.target.value)} />
                </label>
              </div>
            </>
          )}
        </>
      )}

      {/* Admin powers — promote to Event Admin + the permission matrix — are
          admin-only. A non-admin adds plain members (who can also add users). */}
      {isAdmin ? (
        <>
          <label className="field">Designation
            <input value={form.Designation ?? ''} onChange={(e) => setF('Designation', normaliseText(e.target.value))} />
          </label>
          {/* Two-sided event: a member invites for exactly one side and every
              guest they add gets it. Admins work across both (Side stays 'Common'). */}
          {isMarriage && !form.IsAdmin && (
            <div className="pp-seg-field">Invites for side *
              <div className="pp-seg">
                {['Groom', 'Bride'].map(s => (
                  <button key={s} type="button" style={{ padding: 11 }} className={form.Side === s ? 'on' : ''} onClick={() => setF('Side', s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
          <div className="pp-taps">
            <button type="button" className={'pp-tap' + (form.IsAdmin ? ' on' : '')} onClick={() => setF('IsAdmin', !form.IsAdmin)}>
              <span className="pp-tap-box">{form.IsAdmin ? '✓' : ''}</span>
              Event Admin (full access)
            </button>
          </div>

          <div className="pp-head-line">
            <span className="step-label">Permissions</span>
          </div>
          <div className="pp-note">
            {form.IsAdmin ? 'Event Admins have full access — per-module permissions are ignored.' : 'Tap what this user may do per module.'}
          </div>
          <div className={'pp-perm-m' + (form.IsAdmin ? ' off' : '')}>
            {MODULES.map(m => (
              <div key={m} className="pp-perm-m-card">
                <div className="pp-perm-m-head">
                  <span>{MODULE_LABELS[m] || m}</span>
                  <button type="button" className="pp-link" onClick={() => setAll(m, !allOn(m))}>{allOn(m) ? 'None' : 'All'}</button>
                </div>
                <div className="pp-perm-m-btns">
                  {ACTIONS.map(a => {
                    const bit = `Can${m}${a}`;
                    const on = !!form.perms[bit];
                    return (
                      <button key={a} type="button" className={on ? 'on' : ''} aria-pressed={on}
                        onClick={() => setPerm(bit, !on)}>{ACTION_LABELS[a] ?? a}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {REPORT_BITS.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Reports:&nbsp;</strong>
              {REPORT_BITS.map(([bit, lbl]) => (
                <label key={bit} style={{ marginRight: 14, opacity: form.IsAdmin ? 0.5 : 1 }}>
                  <input type="checkbox" checked={!!form.perms[bit]} disabled={form.IsAdmin}
                    onChange={e => setPerm(bit, e.target.checked)} /> {lbl}
                </label>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          This person will be added as a <strong>member</strong> — they can sign in, add their own guests, and add more users.
        </div>
      )}
      {err && <div className="pp-err">{err}</div>}
    </Modal>
  );
}
